import { type EventFilterCondition, type EventFilterType } from '../../../../models/EventFilter'
import { INTEGRATION_DISPLAY_NAME_MAP, type IntegrationServiceId } from '../../../../common/integrations'

type PhraseFactory = (value: string) => string

const formatServiceId = (v: string | null): string => {
  if (v === null || v === 'null') return 'In-person & Phone'
  return INTEGRATION_DISPLAY_NAME_MAP[v as IntegrationServiceId] ?? v.charAt(0).toUpperCase() + v.slice(1)
}

const formatSource = (v: string): string => {
  if (v === 'email') return 'Email'
  return INTEGRATION_DISPLAY_NAME_MAP[v as IntegrationServiceId] ?? v.charAt(0).toUpperCase() + v.slice(1)
}

const formatDuration = (v: string): string => {
  const seconds = parseFloat(v)
  if (isNaN(seconds)) return v
  const hours = parseFloat((seconds / 3600).toFixed(2))
  return hours === 1 ? '1 hour' : `${hours} hours`
}

const PHRASE_MAP: Partial<Record<string, Partial<Record<string, PhraseFactory>>>> = {
  source: {
    equals: (v) => `Join only ${v} meetings`,
    not_equals: (v) => `Skip ${v} meetings`,
  },
  service_id: {
    equals: (v) => `Join only ${v} meetings`,
    not_equals: (v) => `Skip ${v} meetings`,
  },
  host: {
    me: () => 'Join only meetings you host',
    not_me: () => 'Skip meetings you host',
  },
  is_hosted: {
    equals: () => 'Join only meetings you host',
    not_equals: () => 'Skip meetings you host',
  },
  wealthbox_event_type: {
    equals: (v) => `Join only Wealthbox ${v} events`,
    not_equals: (v) => `Skip Wealthbox ${v} events`,
  },
  redtail_type: {
    equals: (v) => `Join only Redtail ${v} activities`,
    not_equals: (v) => `Skip Redtail ${v} activities`,
  },
  redtail_category: {
    equals: (v) => `Join only Redtail ${v} category events`,
    not_equals: (v) => `Skip Redtail ${v} category events`,
  },
  title: {
    equals: (v) => `Join only meetings titled "${v}"`,
    not_equals: (v) => `Skip meetings titled "${v}"`,
    contains: (v) => `Join meetings with "${v}" in the title`,
    not_contains: (v) => `Skip meetings with "${v}" in the title`,
    starts_with: (v) => `Join meetings whose title starts with "${v}"`,
    not_starts_with: (v) => `Skip meetings whose title starts with "${v}"`,
    ends_with: (v) => `Join meetings whose title ends with "${v}"`,
    not_ends_with: (v) => `Skip meetings whose title ends with "${v}"`,
  },
  participant_count: {
    equals: (v) => `Join only meetings with exactly ${v} participants`,
    not_equals: (v) => `Skip meetings with exactly ${v} participants`,
    less_than: (v) => `Join meetings with fewer than ${v} participants`,
    not_less_than: (v) => `Join meetings with ${v} or more participants`,
    greater_than: (v) => `Join meetings with more than ${v} participants`,
    not_greater_than: (v) => `Join meetings with up to ${v} participants`,
  },
  duration: {
    equals: (v) => `Join only meetings lasting exactly ${v}`,
    not_equals: (v) => `Skip meetings lasting exactly ${v}`,
    less_than: (v) => `Join meetings under ${v}`,
    not_less_than: (v) => `Join meetings of ${v} or more`,
    greater_than: (v) => `Join meetings over ${v}`,
    not_greater_than: (v) => `Join meetings up to ${v}`,
  },
}

export type EntityLabelMap = Partial<Record<string, Record<string, string>>>

const NEGATION_MAP: Partial<Record<string, string>> = {
  me: 'not_me',
  not_me: 'me',
  equals: 'not_equals',
  not_equals: 'equals',
  contains: 'not_contains',
  not_contains: 'contains',
  starts_with: 'not_starts_with',
  not_starts_with: 'starts_with',
  ends_with: 'not_ends_with',
  not_ends_with: 'ends_with',
  less_than: 'not_less_than',
  not_less_than: 'less_than',
  greater_than: 'not_greater_than',
  not_greater_than: 'greater_than',
}

const VALUELESS_CONDITION_TYPES = new Set<string>(['host'])

export const isFilterEffectivelyDisabled = (conditions: EventFilterCondition[]): boolean =>
  conditions.some((condition) => {
    const negatedOp = NEGATION_MAP[condition.operator]
    if (!negatedOp) return false
    return conditions.some(
      (other) =>
        other.id !== condition.id &&
        other.type === condition.type &&
        other.operator === negatedOp &&
        (VALUELESS_CONDITION_TYPES.has(condition.type) || condition.value.some((v) => other.value.includes(v))),
    )
  })

const ENTITY_LABEL_TYPES = new Set(['wealthbox_event_type', 'redtail_type', 'redtail_category'])

export const conditionToActivePhrase = (condition: EventFilterCondition, filterType: EventFilterType, entityLabels?: EntityLabelMap): string => {
  const typeMap = PHRASE_MAP[condition.type]
  const phraseFactory = typeMap?.[condition.operator]
  if (!phraseFactory) {
    return `${condition.type} ${condition.operator} ${condition.value.join(', ')}`
  }

  let displayValue: string
  if (condition.type === 'service_id') {
    displayValue = condition.value.map((v) => formatServiceId(v as string | null)).join(', ')
  } else if (condition.type === 'source') {
    displayValue = condition.value.map(formatSource).join(', ')
  } else if (condition.type === 'duration') {
    displayValue = formatDuration(condition.value[0] ?? '0')
  } else if (ENTITY_LABEL_TYPES.has(condition.type) && entityLabels) {
    const labelMap = entityLabels[condition.type] ?? {}
    displayValue = condition.value.map((id) => labelMap[id] ?? id).join(', ')
  } else {
    displayValue = condition.value[0] ?? ''
  }

  const phrase = phraseFactory(displayValue)
  return filterType === 'import_only' ? phrase.replace(/\bJoin\b/g, 'Sync') : phrase
}
